import type { CustomerProfile } from '@najah/core'

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000'
const REFRESH_KEY = 'najah.customer.refreshToken'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code: string = 'ERROR',
    public readonly fields?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}

export function setRefreshToken(token: string | null) {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token)
    else localStorage.removeItem(REFRESH_KEY)
  } catch {
    // تخزين معطّل — تبقى الجلسة حتى إغلاق التطبيق
  }
}

let onSessionExpired: (() => void) | null = null
export function setSessionExpiredHandler(fn: (() => void) | null) {
  onSessionExpired = fn
}

async function parseError(res: Response): Promise<ApiError> {
  let body: { message?: string; code?: string; fields?: Record<string, string> } = {}
  try {
    body = await res.json()
  } catch {
    // الخادم لم يُرجع JSON
  }
  return new ApiError(res.status, body.message ?? 'حدث خطأ غير متوقع', body.code ?? 'ERROR', body.fields)
}

let refreshInFlight: Promise<string> | null = null

async function performRefresh(): Promise<string> {
  const token = getRefreshToken()
  if (!token) throw new ApiError(401, 'سجّل الدخول للمتابعة', 'SESSION_EXPIRED')

  const res = await fetch(`${BASE_URL}/api/storefront/auth/refresh`, {
    signal: AbortSignal.timeout(15_000),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: token }),
  })

  if (!res.ok) {
    setRefreshToken(null)
    setAccessToken(null)
    onSessionExpired?.()
    throw await parseError(res)
  }

  const data = (await res.json()) as { accessToken: string; refreshToken: string }
  setAccessToken(data.accessToken)
  setRefreshToken(data.refreshToken)
  return data.accessToken
}

function refreshOnce(): Promise<string> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  /** مسار عام: يُرسَل الرمز إن وُجد لكن لا يُطلب */
  optionalAuth?: boolean
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query } = options

  const url = new URL(`${BASE_URL}/api/storefront${path}`)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
    }
  }

  const send = async (token: string | null): Promise<Response> =>
    fetch(url, {
      signal: AbortSignal.timeout(15_000),
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

  let res: Response
  try {
    res = await send(accessToken)
  } catch {
    throw new ApiError(0, 'تعذّر الاتصال — تحقّق من الإنترنت', 'NETWORK_ERROR')
  }

  if (res.status === 401 && getRefreshToken()) {
    try {
      res = await send(await refreshOnce())
    } catch (err) {
      throw err instanceof ApiError ? err : new ApiError(401, 'انتهت الجلسة', 'SESSION_EXPIRED')
    }
  }

  if (!res.ok) throw await parseError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/* ── الأنواع ── */
export interface StoreProduct {
  id: string
  sku: string
  nameAr: string
  nameFr: string | null
  description: string | null
  unit: string
  retailPrice: number
  images: string[]
  categoryId: string | null
  categoryName: string | null
  inStock: boolean
  isFavorite?: boolean
  book: { author: string | null; publisher: string | null; pages: number | null; isbn: string | null } | null
}

export interface StoreCategory {
  id: string
  nameAr: string
  parentId: string | null
  icon: string | null
  productCount: number
}

export interface Paged<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface AuthResponse {
  accessToken: string
  refreshToken: string
  customer: CustomerProfile
}
